<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ShellHeader from './ShellHeader.vue'
import ShellSidebar from './ShellSidebar.vue'
import ShellTabs from './ShellTabs.vue'
import AdminCommandPalette from './AdminCommandPalette.vue'
import type { AdminLayoutMenuGroup, AdminLayoutMenuItem, AdminLayoutWorkspaceTab } from '../../types'

const props = withDefaults(
  defineProps<{
    brandTitle: string
    brandSubtitle?: string
    brandMark?: string
    breadcrumb: string
    title: string
    username?: string
    loading?: boolean
    menuGroups: AdminLayoutMenuGroup[]
    workspaceTabs: AdminLayoutWorkspaceTab[]
    activeWorkspaceTabId: string
    showRefresh?: boolean
    showLogout?: boolean
  }>(),
  {
    brandSubtitle: '',
    brandMark: 'A',
    username: '',
    loading: false,
    showRefresh: true,
    showLogout: true
  }
)

const emit = defineEmits<{
  refresh: []
  logout: []
  'menu-item-click': [item: AdminLayoutMenuItem, group: AdminLayoutMenuGroup]
  'workspace-tab-click': [tab: AdminLayoutWorkspaceTab]
  'workspace-tab-close': [tab: AdminLayoutWorkspaceTab]
  'workspace-tab-refresh': [tab: AdminLayoutWorkspaceTab]
  'workspace-close-other': [tab?: AdminLayoutWorkspaceTab]
  'workspace-close-all': []
  'workspace-close-left': [tab: AdminLayoutWorkspaceTab]
  'workspace-close-right': [tab: AdminLayoutWorkspaceTab]
}>()

const sidebarCollapsed = ref(
  typeof window !== 'undefined' && window.innerWidth > 720 && window.innerWidth <= 1180
)
const sidebarPreferenceTouched = ref(false)
const mobileMenuOpen = ref(false)
const workspaceRef = ref<HTMLElement | null>(null)
const commandPaletteOpen = ref(false)

function toggleSidebar() {
  sidebarPreferenceTouched.value = true
  sidebarCollapsed.value = !sidebarCollapsed.value
}

function handleMenuClick(item: AdminLayoutMenuItem, group: AdminLayoutMenuGroup) {
  emit('menu-item-click', item, group)
  mobileMenuOpen.value = false
  resetMainScroll()
}

function handleWorkspaceTabClick(tab: AdminLayoutWorkspaceTab) {
  emit('workspace-tab-click', tab)
  resetMainScroll()
}

function resetMainScroll() {
  if (typeof window !== 'undefined') {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      workspaceRef.value?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    })
  }
}

function syncResponsiveSidebar() {
  if (sidebarPreferenceTouched.value) return
  if (typeof window !== 'undefined') {
    const viewportWidth = window.innerWidth
    sidebarCollapsed.value = viewportWidth > 720 && viewportWidth <= 1180
  }
}

function handleGlobalKeydown(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
    event.preventDefault()
    commandPaletteOpen.value = !commandPaletteOpen.value
  }
  if (event.key === 'Escape') commandPaletteOpen.value = false
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncResponsiveSidebar)
    window.addEventListener('keydown', handleGlobalKeydown)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', syncResponsiveSidebar)
    window.removeEventListener('keydown', handleGlobalKeydown)
  }
})

watch(
  () => props.activeWorkspaceTabId,
  () => {
    void nextTick(() => resetMainScroll())
  },
  { immediate: true }
)
</script>

<template>
  <main
    class="admin-shell rr-shell"
    :class="{
      'is-collapsed': sidebarCollapsed,
      'is-mobile-open': mobileMenuOpen,
      'is-loading': loading
    }"
  >
    <!-- Topbar Header -->
    <ShellHeader
      :brand-title="brandTitle"
      :brand-subtitle="brandSubtitle"
      :brand-mark="brandMark"
      :breadcrumb="breadcrumb"
      :title="title"
      :username="username"
      :loading="loading"
      :show-refresh="showRefresh"
      :show-logout="showLogout"
      :sidebar-collapsed="sidebarCollapsed"
      :mobile-menu-open="mobileMenuOpen"
      @toggle-sidebar="toggleSidebar"
      @toggle-mobile-menu="mobileMenuOpen = !mobileMenuOpen"
      @open-command-palette="commandPaletteOpen = true"
      @refresh="emit('refresh')"
      @logout="emit('logout')"
    >
      <template v-if="$slots['header-actions']" #header-actions>
        <slot name="header-actions" />
      </template>
    </ShellHeader>

    <!-- Sidebar Navigation -->
    <ShellSidebar
      :menu-groups="menuGroups"
      :sidebar-collapsed="sidebarCollapsed"
      :mobile-menu-open="mobileMenuOpen"
      :active-workspace-tab-id="activeWorkspaceTabId"
      @menu-item-click="handleMenuClick"
      @update:sidebar-collapsed="sidebarCollapsed = $event"
      @update:mobile-menu-open="mobileMenuOpen = $event"
    />

    <!-- Mobile Mask -->
    <button
      v-if="mobileMenuOpen"
      type="button"
      class="admin-shell__mask"
      aria-label="关闭菜单"
      @click="mobileMenuOpen = false"
    />

    <!-- Main Workspace -->
    <section ref="workspaceRef" class="admin-shell__workspace">
      <!-- Tabs Navigation -->
      <ShellTabs
        v-if="workspaceTabs && workspaceTabs.length"
        :workspace-tabs="workspaceTabs"
        :active-workspace-tab-id="activeWorkspaceTabId"
        @tab-click="handleWorkspaceTabClick"
        @tab-close="emit('workspace-tab-close', $event)"
        @tab-refresh="emit('workspace-tab-refresh', $event)"
        @close-other="emit('workspace-close-other', $event)"
        @close-all="emit('workspace-close-all')"
        @close-left="emit('workspace-close-left', $event)"
        @close-right="emit('workspace-close-right', $event)"
      />

      <!-- Content Body Slot -->
      <div class="admin-shell__body">
        <slot />
      </div>
    </section>

    <!-- Command Palette -->
    <AdminCommandPalette
      :open="commandPaletteOpen"
      :groups="menuGroups"
      @close="commandPaletteOpen = false"
      @select="handleMenuClick"
    />
  </main>
</template>

<style scoped>
.admin-shell {
  --admin-shell-topbar-height: 50px;
  --admin-shell-sidebar-width: 230px;
  position: relative;
  height: 100vh;
  min-height: 100vh;
  background: var(--rr-color-canvas);
  color: var(--rr-color-text-strong);
  font-family: inherit;
  overflow: hidden;
}

.admin-shell.is-collapsed {
  --admin-shell-sidebar-width: 64px;
}

.admin-shell__mask {
  position: fixed;
  inset: 0;
  z-index: 110;
  border: 0;
  background: rgba(15, 17, 21, 0.45);
  cursor: pointer;
  display: none;
}

.admin-shell__workspace {
  margin-top: var(--admin-shell-topbar-height, 50px);
  margin-left: var(--admin-shell-sidebar-width, 230px);
  height: calc(100vh - var(--admin-shell-topbar-height, 50px));
  overflow-y: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: var(--rr-color-canvas);
  transition: margin-left 0.24s ease;
}

.admin-shell__body {
  flex: 1 1 auto;
  padding: 16px;
  box-sizing: border-box;
}

@media (max-width: 720px) {
  .admin-shell__workspace {
    margin-left: 0;
  }
  .admin-shell.is-mobile-open .admin-shell__mask {
    display: block;
  }
  .admin-shell :deep(.admin-shell__sidebar) {
    transform: translateX(-100%);
  }
  .admin-shell.is-mobile-open :deep(.admin-shell__sidebar) {
    transform: translateX(0);
  }
}
</style>
